import type {
  RuleBuilderRule,
  RuleTree,
} from 'flow/service/rules';
import {
  buildRuleTree,
  getRulePositionKey,
  isBooleanRule,
  isGroupRule,
  isThresholdRule,
} from 'flow/service/rules';
import type { Action, PublicId } from 'flow/store/engine/types';
import type { ChangelogInputBundle } from '../../types';


function generateTree(action: Action): RuleTree | null {
  if (!action.rules || !isBooleanRule(action.rules)) {
    return null;
  }

  return buildRuleTree(action.rules);
}

/**
 * Flattens a rule tree into a map of threshold publicId -> position key
 */
function getRulePositions(actionTree: RuleTree | null) {
  const positions: Record<PublicId, string> = {};

  if (!actionTree) {
    return positions;
  }

  function visit(rule: RuleBuilderRule) {
    if (isThresholdRule(rule)) {
      positions[rule.id] = getRulePositionKey(rule.data.path);
      return;
    }
    if (isGroupRule(rule)) {
      rule.data.children.forEach(visit);
    }
  }

  visit(actionTree);
  return positions;
}

/**
 * An action that is present in both the before and after snapshot
 */
type ModifiedActionPair = {
  initial: Action;
  current: Action;
};

/**
 * Filters the out non-action public ids from the modified set, and creates
 * before/after action pairs.
 */
function getModifiedActionPairs(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  modified: Set<PublicId>;
}): ModifiedActionPair[] {
  const { A, B, modified } = args;
  const pairs: ModifiedActionPair[] = [];

  for (const publicId of modified) {
    const initial = B.version?.actions?.[publicId];
    const current = A.version?.actions?.[publicId];

    if (initial && current) {
      pairs.push({ initial, current });
    }
  }

  return pairs;
}


type DiffRulePositionsProps = {
  initial: Record<PublicId, string>;
  current: Record<PublicId, string>;
};

/**
 * Compares a before and after snapshot of threshold positions and returns the
 * publicIds of thresholds that occupy a different slot in the rule tree.
 */
function diffRulePositions({ initial, current }: DiffRulePositionsProps): PublicId[] {
  const moved: PublicId[] = [];

  for (const [publicId, initialPositionKey] of Object.entries(initial)) {
    const currentPositionKey = current[publicId];

    if (currentPositionKey && currentPositionKey !== initialPositionKey) {
      moved.push(publicId);
    }
  }

  return moved;
}

type DiffThresholdMovesProps = {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  modified: Set<PublicId>;
};

/**
 * Takes in an A(fter) and B(efore) changelogInputBundle and returns the set of
 * thresholds that moved position within a modified action's rule tree.
 *
 * - Assumes each threshold can only have one path in a given rule tree.
 * - Assumes `generateMergeDiff` identified all added, modified, and removed thresholds
 */
export function diffThresholdMoves({ A, B, modified }: DiffThresholdMovesProps): Set<PublicId> {
  const movedThresholds = new Set<PublicId>();

  for (const { initial, current } of getModifiedActionPairs({ A, B, modified })) {
    const moved = diffRulePositions({
      initial: getRulePositions(generateTree(initial)),
      current: getRulePositions(generateTree(current)),
    });

    moved.forEach(publicId => movedThresholds.add(publicId));
  }

  return movedThresholds;
}
